import { useState, useEffect } from "react";
import { useAuth } from "../../context/AuthContext";
import { useTheme } from "../../context/ThemeContext";
import styles from "./PricingManagement.module.css";

const ROLES = ["customer", "reseller", "vip1", "vip2", "vip3"];

export const PricingManagement = () => {
    const { theme } = useTheme();
    const API_URL = import.meta.env.VITE_API_URL;
    const token = useAuth().token;

  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [success, setSuccess] = useState("");
  const [pricingSettings, setPricingSettings] = useState(null);

  // Cost ranges state
  const [costRanges, setCostRanges] = useState([]);
  const [editingRange, setEditingRange] = useState(null);
  const [rangeForm, setRangeForm] = useState({
    name: "",
    min: "",
    max: "",
    currency: "INR",
  });

  // Margins state - restructured to handle API format
  const [marginProfiles, setMarginProfiles] = useState({});
  const [editingMargin, setEditingMargin] = useState(null);
  const [marginForm, setMarginForm] = useState({
    range: "",
    margin: "",
    role: "",
  });

  // Fetch pricing settings on mount
  useEffect(() => {
    fetchPricingSettings();
  }, []);

  const fetchPricingSettings = async () => {
    try {
      setLoading(true);
      const response = await fetch(`${API_URL}/admin/pricing/settings`, {
        headers: { Authorization: `Bearer ${token}` },
      });

      if (!response.ok) throw new Error("Failed to fetch pricing settings");

      const data = await response.json();
      console.log("📋 API Response:", data);

      if (data.success && data.data) {
        setPricingSettings(data.data);
        setCostRanges(data.data.costRanges || []);

        // Transform API response to normalized format
        const normalizedMargins = transformMarginProfiles(
          data.data.marginProfiles,
        );
        setMarginProfiles(normalizedMargins);
        console.log("✅ Normalized margins:", normalizedMargins);

        setSuccess("Pricing settings loaded");
      } else {
        throw new Error(data.message || "Invalid response format");
      }
    } catch (err) {
      console.error("❌ Error:", err);
      setError(`Failed to load pricing settings: ${err.message}`);
    } finally {
      setLoading(false);
      setTimeout(() => setSuccess(""), 3000);
    }
  };

  /**
   * Transform API response marginProfiles to normalized format
   * Handles both flat object (customer, reseller) and array (vipTiers)
   */
  const transformMarginProfiles = (apiMarginProfiles) => {
    const normalized = {};

    // Handle customer and reseller (flat object structure)
    if (apiMarginProfiles.customer) {
      normalized.customer = {
        defaultMargin: apiMarginProfiles.customer.defaultMargin,
        rangeMargins: apiMarginProfiles.customer.rangeMargins || [],
        isActive: apiMarginProfiles.customer.isActive,
      };
    }

    if (apiMarginProfiles.reseller) {
      normalized.reseller = {
        defaultMargin: apiMarginProfiles.reseller.defaultMargin,
        rangeMargins: apiMarginProfiles.reseller.rangeMargins || [],
        isActive: apiMarginProfiles.reseller.isActive,
      };
    }

    // Handle VIP tiers (array structure)
    if (
      apiMarginProfiles.vipTiers &&
      Array.isArray(apiMarginProfiles.vipTiers)
    ) {
      apiMarginProfiles.vipTiers.forEach((vipTier) => {
        const tierName = vipTier.tier; // e.g., 'vip1', 'vip2', 'vip3'
        normalized[tierName] = {
          defaultMargin: vipTier.additionalDiscount || 0, // Use additionalDiscount as default
          rangeMargins: vipTier.rangeMargins || [],
          isActive: vipTier.isActive,
          tier: vipTier.tier,
        };
      });
    }

    return normalized;
  };

  // Cost Range Management
  const handleAddRange = () => {
    setEditingRange(null);
    setRangeForm({ name: "", min: "", max: "", currency: "INR" });
  };

  const handleEditRange = (range) => {
    setEditingRange(range._id);
    setRangeForm({
      name: range.name,
      min: range.min,
      max: range.max,
      currency: range.currency,
    });
  };

  const handleSaveRange = async () => {
    if (!rangeForm.name || rangeForm.min === "" || rangeForm.max === "") {
      setError("Please fill all range fields");
      return;
    }

    if (Number(rangeForm.min) > Number(rangeForm.max)) {
      setError("Min value cannot be greater than max value");
      return;
    }

    try {
      setLoading(true);
      const url = editingRange
        ? `${API_URL}/admin/pricing/ranges/${editingRange}`
        : `${API_URL}/admin/pricing/ranges`;

      const method = editingRange ? "PUT" : "POST";

      const response = await fetch(url, {
        method,
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify({
          name: rangeForm.name,
          min: Number(rangeForm.min),
          max: Number(rangeForm.max),
          currency: rangeForm.currency,
          isActive: true,
        }),
      });

      if (!response.ok) throw new Error("Failed to save range");

      setSuccess(`Range ${editingRange ? "updated" : "created"} successfully!`);
      await fetchPricingSettings();
      setRangeForm({ name: "", min: "", max: "", currency: "INR" });
      setEditingRange(null);
    } catch (err) {
      setError(`Failed to save range: ${err.message}`);
    } finally {
      setLoading(false);
      setTimeout(() => setSuccess(""), 3000);
    }
  };

  const handleDeleteRange = async (rangeId) => {
    if (!window.confirm("Are you sure you want to delete this range?")) return;

    try {
      setLoading(true);
      const response = await fetch(
        `${API_URL}/admin/pricing/ranges/${rangeId}`,
        {
          method: "DELETE",
          headers: { Authorization: `Bearer ${token}` },
        },
      );

      if (!response.ok) throw new Error("Failed to delete range");

      setSuccess("Range deleted successfully!");
      await fetchPricingSettings();
    } catch (err) {
      setError(`Failed to delete range: ${err.message}`);
    } finally {
      setLoading(false);
      setTimeout(() => setSuccess(""), 3000);
    }
  };

  // Margin Management
  const handleAddMargin = (role) => {
    setEditingMargin(null);
    setMarginForm({ range: "", margin: "", role });
  };

  const handleEditMargin = (role, rangeMargin) => {
    setEditingMargin(`${role}-${rangeMargin.range}`);
    setMarginForm({
      range: rangeMargin.range,
      margin: rangeMargin.margin,
      role,
    });
  };

  const handleSaveMargin = async () => {
    if (!marginForm.range || marginForm.margin === "") {
      setError("Please fill all margin fields");
      return;
    }

    if (Number(marginForm.margin) < 0 || Number(marginForm.margin) > 100) {
      setError("Margin must be between 0 and 100");
      return;
    }

    try {
      setLoading(true);
      const response = await fetch(`${API_URL}/admin/pricing/margins`, {
        method: "PUT",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify({
          role: marginForm.role,
          range: marginForm.range,
          margin: Number(marginForm.margin),
        }),
      });

      if (!response.ok) throw new Error("Failed to save margin");

      setSuccess("Margin updated successfully!");
      await fetchPricingSettings();
      setMarginForm({ range: "", margin: "", role: "" });
      setEditingMargin(null);
    } catch (err) {
      setError(`Failed to save margin: ${err.message}`);
    } finally {
      setLoading(false);
      setTimeout(() => setSuccess(""), 3000);
    }
  };

  const handleCancelEdit = () => {
    setEditingRange(null);
    setEditingMargin(null);
    setRangeForm({ name: "", min: "", max: "", currency: "INR" });
    setMarginForm({ range: "", margin: "", role: "" });
  };

  if (loading && !pricingSettings) {
    return <div className={styles.loading}>Loading pricing settings...</div>;
  }

  return (
    <div
      className={`${styles.container} ${theme === "dark" ? styles.dark : ""}`}
    >
      <h2>💰 Pricing Management</h2>

      {/* Alert Messages */}
      {error && (
        <div className={`${styles.alert} ${styles.error}`}>{error}</div>
      )}
      {success && (
        <div className={`${styles.alert} ${styles.success}`}>{success}</div>
      )}

      {/* Cost Ranges Section */}
      <section className={styles.section}>
        <div className={styles.sectionHeader}>
          <h3>📊 Cost Ranges</h3>
          {editingRange === null && (
            <button className={styles.buttonPrimary} onClick={handleAddRange}>
              + Add Range
            </button>
          )}
        </div>

        {/* Range Form */}
        {editingRange !== null ||
        (editingRange === null && editingMargin === null) ? (
          editingRange !== null || rangeForm.name ? (
            <div className={styles.formCard}>
              <h4>{editingRange ? "Edit" : "Add"} Cost Range</h4>
              <div className="row g-2">
                <div className="col-md-3">
                  <label className={styles.formLabel}>Range Name *</label>
                  <input
                    type="text"
                    className={styles.formControl}
                    placeholder="e.g., small, medium, large"
                    value={rangeForm.name}
                    onChange={(e) =>
                      setRangeForm({ ...rangeForm, name: e.target.value })
                    }
                  />
                </div>
                <div className="col-md-2">
                  <label className={styles.formLabel}>Min Value *</label>
                  <input
                    type="number"
                    className={styles.formControl}
                    placeholder="0"
                    value={rangeForm.min}
                    onChange={(e) =>
                      setRangeForm({ ...rangeForm, min: e.target.value })
                    }
                  />
                </div>
                <div className="col-md-2">
                  <label className={styles.formLabel}>Max Value *</label>
                  <input
                    type="number"
                    className={styles.formControl}
                    placeholder="200"
                    value={rangeForm.max}
                    onChange={(e) =>
                      setRangeForm({ ...rangeForm, max: e.target.value })
                    }
                  />
                </div>
                <div className="col-md-2">
                  <label className={styles.formLabel}>Currency</label>
                  <input
                    type="text"
                    className={styles.formControl}
                    value={rangeForm.currency}
                    disabled
                  />
                </div>
                <div className="col-md-3 d-flex gap-2 align-items-end">
                  <button
                    className={styles.buttonSuccess}
                    onClick={handleSaveRange}
                    disabled={loading}
                  >
                    {loading ? "Saving..." : "Save Range"}
                  </button>
                  <button
                    className={styles.buttonSecondary}
                    onClick={handleCancelEdit}
                    disabled={loading}
                  >
                    Cancel
                  </button>
                </div>
              </div>
            </div>
          ) : null
        ) : null}

        {/* Cost Ranges Table */}
        <div className={styles.tableContainer}>
          <table className={styles.table}>
            <thead>
              <tr>
                <th>Range Name</th>
                <th>Min Value</th>
                <th>Max Value</th>
                <th>Currency</th>
                <th>Status</th>
                <th>Actions</th>
              </tr>
            </thead>
            <tbody>
              {costRanges && costRanges.length > 0 ? (
                costRanges.map((range) => (
                  <tr key={range._id}>
                    <td>{range.name}</td>
                    <td>{range.min}</td>
                    <td>{range.max}</td>
                    <td>{range.currency}</td>
                    <td>
                      <span
                        className={
                          range.isActive ? styles.active : styles.inactive
                        }
                      >
                        {range.isActive ? "Active" : "Inactive"}
                      </span>
                    </td>
                    <td>
                      <button
                        className={styles.buttonSmall}
                        onClick={() => handleEditRange(range)}
                      >
                        Edit
                      </button>
                      <button
                        className={`${styles.buttonSmall} ${styles.buttonDanger}`}
                        onClick={() => handleDeleteRange(range._id)}
                      >
                        Delete
                      </button>
                    </td>
                  </tr>
                ))
              ) : (
                <tr>
                  <td colSpan="6" className={styles.emptyState}>
                    No cost ranges found
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
      </section>

      {/* Margin Profiles Section */}
      <section className={styles.section}>
        <h3>📈 Margin Profiles</h3>

        {/* Margin Form */}
        {editingMargin || marginForm.role ? (
          <div className={styles.formCard}>
            <h4>Edit Margin</h4>
            <div className="row g-2">
              <div className="col-md-3">
                <label className={styles.formLabel}>Role</label>
                <input
                  type="text"
                  className={styles.formControl}
                  value={marginForm.role}
                  disabled
                />
              </div>
              <div className="col-md-3">
                <label className={styles.formLabel}>Range *</label>
                <select
                  className={styles.formControl}
                  value={marginForm.range}
                  onChange={(e) =>
                    setMarginForm({ ...marginForm, range: e.target.value })
                  }
                  disabled={editingMargin !== null}
                >
                  <option value="">Select Range</option>
                  {costRanges.map((range) => (
                    <option key={range._id} value={range.name}>
                      {range.name}
                    </option>
                  ))}
                </select>
              </div>
              <div className="col-md-3">
                <label className={styles.formLabel}>Margin (%) *</label>
                <input
                  type="number"
                  className={styles.formControl}
                  placeholder="20"
                  value={marginForm.margin}
                  onChange={(e) =>
                    setMarginForm({ ...marginForm, margin: e.target.value })
                  }
                />
              </div>
              <div className="col-md-3 d-flex gap-2 align-items-end">
                <button
                  className={styles.buttonSuccess}
                  onClick={handleSaveMargin}
                  disabled={loading}
                >
                  {loading ? "Saving..." : "Save Margin"}
                </button>
                <button
                  className={styles.buttonSecondary}
                  onClick={handleCancelEdit}
                  disabled={loading}
                >
                  Cancel
                </button>
              </div>
            </div>
          </div>
        ) : null}

        {/* Margin Tables by Role */}
        {ROLES.map((role) => {
          const roleData = marginProfiles[role];

          // Skip if role data doesn't exist
          if (!roleData) {
            console.warn(`⚠️ No data for role: ${role}`);
            return null;
          }

          return (
            <div key={role} className={styles.marginRoleCard}>
              <div className={styles.roleHeader}>
                <h5 className={styles.roleTitle}>
                  {role.toUpperCase()}
                  {role === "customer" && " 🛍️"}
                  {role === "reseller" && " 🤝"}
                  {role.startsWith("vip") && " 👑"}
                </h5>
                <button
                  className={styles.buttonSmall}
                  onClick={() => handleAddMargin(role)}
                >
                  + Add Margin
                </button>
              </div>

              <div className={styles.tableContainer}>
                <table className={styles.table}>
                  <thead>
                    <tr>
                      <th>Range</th>
                      <th>Margin (%)</th>
                      <th>Actions</th>
                    </tr>
                  </thead>
                  <tbody>
                    {roleData.rangeMargins &&
                    roleData.rangeMargins.length > 0 ? (
                      roleData.rangeMargins.map((rm) => (
                        <tr key={`${role}-${rm.range}`}>
                          <td>{rm.range}</td>
                          <td>{rm.margin}%</td>
                          <td>
                            <button
                              className={styles.buttonSmall}
                              onClick={() => handleEditMargin(role, rm)}
                            >
                              Edit
                            </button>
                          </td>
                        </tr>
                      ))
                    ) : (
                      <tr>
                        <td colSpan="3" className={styles.emptyState}>
                          No margins configured
                        </td>
                      </tr>
                    )}
                  </tbody>
                </table>
              </div>

              {/* Default Margin */}
              <div className={styles.defaultMarginInfo}>
                <small>
                  Default Margin:{" "}
                  <strong>{roleData.defaultMargin || "0"}%</strong>
                </small>
              </div>
            </div>
          );
        })}
      </section>

      {/* Settings Summary */}
      {pricingSettings && (
        <section className={styles.section}>
          <h3>ℹ️ Settings Summary</h3>
          <div className={styles.summaryGrid}>
            <div className={styles.summaryCard}>
              <h6>Cost Ranges</h6>
              <p className={styles.summaryValue}>{costRanges.length}</p>
            </div>
            <div className={styles.summaryCard}>
              <h6>Active Ranges</h6>
              <p className={styles.summaryValue}>
                {costRanges.filter((r) => r.isActive).length}
              </p>
            </div>
            <div className={styles.summaryCard}>
              <h6>Margin Roles</h6>
              <p className={styles.summaryValue}>
                {Object.keys(marginProfiles).length}
              </p>
            </div>
            <div className={styles.summaryCard}>
              <h6>Last Updated</h6>
              <p className={styles.summaryValue}>
                {pricingSettings.lastUpdatedAt
                  ? new Date(pricingSettings.lastUpdatedAt).toLocaleDateString()
                  : "N/A"}
              </p>
            </div>
          </div>
        </section>
      )}
    </div>
  );
};

export default PricingManagement;
